// Thin API client. Reads VITE_API_URL at build time (defaults to the
// production hostname). Adds Bearer auth from the in-memory store, and
// transparently refreshes the access token on 401 by hitting /auth/refresh
// with the stored refresh token. A single concurrent refresh is shared
// across overlapping requests via a queued promise.
import { useAuthStore } from './auth';

// Default to '' (relative) so the SPA's nginx can same-origin-proxy
// `/api/*` to the backend container — avoids CORS, works under SSH
// tunnel, and works on api.scorelync.com once DNS is wired. Override
// with VITE_API_URL at build time for split-origin deployments.
export const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
}

export type OrgStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED';
export type UserRole =
  | 'OWNER'
  | 'ADMIN'
  | 'OPERATOR'
  | 'STATS_KEEPER'
  | 'VIEWER';

export interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: UserRole;
    isPlatformAdmin: boolean;
    lastLoginAt: string | null;
    createdAt: string;
  };
  org: {
    id: string;
    name: string;
    slug: string;
    status: OrgStatus;
    planTier: string;
  };
}

export interface PendingOrg {
  id: string;
  name: string;
  slug: string;
  status: OrgStatus;
  planTier: string;
  createdAt: string;
  users: { id: string; email: string; name: string; createdAt: string }[];
  _count: { users: number };
}

export interface Venue {
  id: string;
  orgId: string;
  name: string;
  address: string | null;
  capacity: number | null;
  timezone: string;
  createdAt: string;
  updatedAt: string;
  _count?: { displays: number };
}

export interface Sport {
  id: string;
  name: string;
  slug: string;
  isPhase1: boolean;
  config: unknown;
}

export interface Team {
  id: string;
  orgId: string;
  name: string;
  abbreviation: string | null;
  colorPrimary: string | null;
  colorSecondary: string | null;
  logoUrl: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { players: number };
}

export interface TeamWithRoster extends Team {
  players: Player[];
}

export interface Player {
  id: string;
  teamId: string;
  name: string;
  jerseyNumber: string | null;
  position: string | null;
  photoUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export type DisplayChannel =
  | 'SCOREBOARD'
  | 'AUX_CLOCK'
  | 'STANDINGS'
  | 'STREAM_OVERLAY'
  | 'MENU'
  | 'AD_REEL'
  | 'SIGNAGE'
  | 'STATS_BOARD'
  | 'AD_ONLY';

export type DisplayStatus = 'ONLINE' | 'OFFLINE' | 'PAIRING';

export interface DisplaySummary {
  id: string;
  venueId: string;
  name: string;
  channelType: DisplayChannel;
  resolutionW: number | null;
  resolutionH: number | null;
  orientation: string;
  status: DisplayStatus;
  lastHeartbeat: string | null;
  pairedAt: string | null;
  pairingExpiresAt: string | null;
  displayConfig: ScoreboardConfig | null;
  createdAt: string;
  updatedAt: string;
}

export type ThemeKey =
  | 'classic' | 'maroon' | 'navy' | 'forest' | 'royal'
  | 'arena-led' | 'premium-glass' | 'angular'
  | 'daylight' | 'electric' | 'mono' | 'retro-led' | 'broadcast'
  | 'stadium-gold' | 'carbon' | 'sunset' | 'frost' | 'volt';
export type DigitStyle = 'segment' | 'bulb' | 'broadcast';

export interface ScoreboardConfig {
  theme?: ThemeKey;
  digitStyle?: DigitStyle;
  schoolName?: string;
  homeLogo?: string;
  awayLogo?: string;
  sponsorTop?: { name?: string; tagline?: string };
  sponsorBottom?: { name?: string; tagline?: string };
  /** UUID of the game whose live state should drive this display. */
  assignedGameId?: string;
  /**
   * Scoreboard design: a built-in template key (e.g. `arena-led-full`) or
   * the UUID of a saved org layout. Empty/undefined = the basic scoreboard.
   */
  layoutTemplate?: string;
}

export type GameStatus = 'SCHEDULED' | 'LIVE' | 'FINAL' | 'CANCELLED';

export interface BasketballRules {
  periodCount: number;
  periodLengthSeconds: number;
  overtimeLengthSeconds: number;
  initialTimeouts: number;
  bonusFoulThreshold: number;
  personalFoulLimit: number;
  shotClock: {
    enabled: boolean;
    durationSeconds: number;
    offensiveReboundSeconds: number;
  };
}

export interface RulePreset {
  id: string;
  orgId: string;
  sportId: string;
  name: string;
  config: BasketballRules;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  sport?: { id: string; name: string; slug: string };
}

export interface GameSummary {
  id: string;
  orgId: string;
  status: GameStatus;
  scheduledAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  venue: { id: string; name: string };
  sport: { id: string; name: string; slug: string };
  homeTeam: { id: string; name: string; abbreviation: string | null; colorPrimary: string | null };
  awayTeam: { id: string; name: string; abbreviation: string | null; colorPrimary: string | null };
  currentState: ProjectedGameState | null;
  statsEnabled?: boolean;
  animationConfig?: { enabled?: boolean; disabled?: string[] } | null;
}

export interface GameDetail extends GameSummary {
  homeTeam: GameSummary['homeTeam'] & { players: PlayerInGame[] };
  awayTeam: GameSummary['awayTeam'] & { players: PlayerInGame[] };
  events?: Array<{
    id: string;
    sequence: string;
    eventType: string;
    payload: Record<string, unknown> | null;
    createdAt: string;
    undone: boolean;
  }>;
}

export interface PlayerInGame {
  id: string;
  name: string;
  jerseyNumber: string | null;
  position: string | null;
}

export interface SideProjection {
  score: number;
  fouls: number;
  timeoutsRemaining: number;
  bonus: boolean;
  doubleBonus?: boolean;
}

export interface ProjectedGameState {
  home: SideProjection;
  away: SideProjection;
  period: number;
  clock: {
    m: number;
    s: number;
    running: boolean;
    startedAt?: string | null;
  };
  possession: 'home' | 'away' | null;
  playerStats?: Record<string, Record<string, number>>;
  onCourt: { home: string[]; away: string[] };
  playerStints: Record<string, string>;
  intermission: 'halftime' | 'period_break' | null;
  intermissionEndsAt: string | null;
  foulOutPlayers: string[];
  sportState?: Record<string, unknown>;
}

export interface CreateDisplayResponse {
  display: DisplaySummary;
  pairingCode: string;
  pairingExpiresAt: string;
}

class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body: unknown,
  ) {
    super(message);
  }
}

// ─── Remote pairing session (no-login iPad) ───
const REMOTE_KEY = 'scorelync-remote-session';

export interface RemoteSession {
  token: string;
  gameId: string;
  role: 'SCORER' | 'STATS';
  game: { id: string; homeTeam: string; awayTeam: string; sport: string };
}

function loadRemoteSession(): RemoteSession | null {
  // Read from localStorage first; fall back to sessionStorage, which
  // survives a page refresh in the same tab even when some iOS browser
  // contexts don't persist localStorage.
  for (const store of [
    typeof localStorage !== 'undefined' ? localStorage : null,
    typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  ]) {
    try {
      const raw = store?.getItem(REMOTE_KEY);
      if (raw) return JSON.parse(raw) as RemoteSession;
    } catch {
      // ignore and try the next store
    }
  }
  return null;
}

let remoteSession: RemoteSession | null = loadRemoteSession();
let remoteToken: string | null = remoteSession?.token ?? null;

export function getRemoteSession(): RemoteSession | null {
  return remoteSession;
}

export function setRemoteSession(s: RemoteSession | null): void {
  remoteSession = s;
  remoteToken = s?.token ?? null;
  const value = s ? JSON.stringify(s) : null;
  for (const store of [
    typeof localStorage !== 'undefined' ? localStorage : null,
    typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  ]) {
    try {
      if (value) store?.setItem(REMOTE_KEY, value);
      else store?.removeItem(REMOTE_KEY);
    } catch {
      // storage may be unavailable (private mode) — keep the in-memory copy
    }
  }
}

let refreshInFlight: Promise<void> | null = null;

async function rawFetch(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (init.auth !== false) {
    // A remote pairing session (no-login iPad) takes precedence — its
    // scoped token is the only credential that device has.
    const token = remoteToken ?? useAuthStore.getState().accessToken;
    if (token) headers.set('authorization', `Bearer ${token}`);
  }
  return fetch(`${API_URL}${path}`, { ...init, headers });
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  let res = await rawFetch(path, init);

  if (res.status === 401 && init.auth !== false) {
    const ok = await ensureRefreshed();
    if (ok) res = await rawFetch(path, init);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message =
      (body as { message?: string }).message ?? `HTTP ${res.status}`;
    throw new ApiError(
      res.status,
      (body as { code?: string }).code ?? String(res.status),
      Array.isArray(message) ? message.join(', ') : message,
      body,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function ensureRefreshed(): Promise<boolean> {
  const { refreshToken, setTokens, clear } = useAuthStore.getState();
  if (!refreshToken) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const tokens = await request<TokenPair>('/api/auth/refresh', {
          method: 'POST',
          auth: false,
          body: JSON.stringify({ refreshToken }),
        });
        setTokens(tokens);
      } catch {
        clear();
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  await refreshInFlight;
  return !!useAuthStore.getState().accessToken;
}

// ─── Public auth ───
export const api = {
  signup(dto: {
    email: string;
    password: string;
    name: string;
    orgName: string;
  }) {
    return request<TokenPair>('/api/auth/signup', {
      method: 'POST',
      auth: false,
      body: JSON.stringify(dto),
    });
  },
  login(dto: { email: string; password: string }) {
    return request<TokenPair>('/api/auth/login', {
      method: 'POST',
      auth: false,
      body: JSON.stringify(dto),
    });
  },
  logout(refreshToken: string) {
    return request<void>('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  },
  me() {
    return request<MeResponse>('/api/auth/me');
  },
  // ─── Sports ───
  listSports() {
    return request<Sport[]>('/api/sports');
  },
  // ─── Venues ───
  listVenues() {
    return request<Venue[]>('/api/venues');
  },
  getVenue(id: string) {
    return request<Venue>(`/api/venues/${id}`);
  },
  createVenue(dto: {
    name: string;
    address?: string;
    capacity?: number;
    timezone?: string;
  }) {
    return request<Venue>('/api/venues', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  // ─── Teams ───
  listTeams() {
    return request<Team[]>('/api/teams');
  },
  getTeam(id: string) {
    return request<TeamWithRoster>(`/api/teams/${id}`);
  },
  createTeam(dto: {
    name: string;
    abbreviation?: string;
    colorPrimary?: string;
    colorSecondary?: string;
  }) {
    return request<Team>('/api/teams', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  updateTeam(
    id: string,
    dto: {
      name?: string;
      abbreviation?: string;
      colorPrimary?: string;
      colorSecondary?: string;
      logoUrl?: string;
    },
  ) {
    return request<Team>(`/api/teams/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },
  addPlayer(
    teamId: string,
    dto: { name: string; jerseyNumber?: string; position?: string },
  ) {
    return request<Player>(`/api/teams/${teamId}/players`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  removePlayer(teamId: string, playerId: string) {
    return request<{ deleted: true }>(
      `/api/teams/${teamId}/players/${playerId}`,
      { method: 'DELETE' },
    );
  },
  importPlayers(teamId: string, csv: string) {
    return request<{
      created: number;
      skipped: number;
      errors: Array<{ line: number; reason: string; raw: string }>;
    }>(`/api/teams/${teamId}/players/import`, {
      method: 'POST',
      body: JSON.stringify({ csv }),
    });
  },
  // ─── Displays ───
  listAllDisplays() {
    return request<(DisplaySummary & { venue: { id: string; name: string } })[]>(
      '/api/displays',
    );
  },
  listVenueDisplays(venueId: string) {
    return request<DisplaySummary[]>(`/api/venues/${venueId}/displays`);
  },
  createDisplay(
    venueId: string,
    dto: { name: string; channelType?: DisplayChannel; orientation?: string },
  ) {
    return request<CreateDisplayResponse>(`/api/venues/${venueId}/displays`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  regeneratePairingCode(displayId: string) {
    return request<{ pairingCode: string; pairingExpiresAt: string }>(
      `/api/displays/${displayId}/regenerate-pairing-code`,
      { method: 'POST' },
    );
  },
  updateDisplay(
    displayId: string,
    dto: {
      name?: string;
      channelType?: DisplayChannel;
      orientation?: 'landscape' | 'portrait';
      displayConfig?: ScoreboardConfig;
    },
  ) {
    return request<DisplaySummary>(`/api/displays/${displayId}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },
  deleteDisplay(displayId: string) {
    return request<{ id: string; deleted: true }>(
      `/api/displays/${displayId}`,
      { method: 'DELETE' },
    );
  },
  // ─── Games ───
  listGames() {
    return request<GameSummary[]>('/api/games');
  },
  getGame(id: string) {
    return request<GameDetail>(`/api/games/${id}`);
  },
  createGame(dto: {
    venueId: string;
    sportId: string;
    homeTeamId: string;
    awayTeamId: string;
    scheduledAt?: string;
    rulePresetId?: string;
    gameConfig?: Record<string, unknown>;
    statsEnabled?: boolean;
  }) {
    return request<GameSummary>('/api/games', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  // ─── Rule presets ───
  listRulePresets(sportId?: string) {
    const qs = sportId ? `?sportId=${encodeURIComponent(sportId)}` : '';
    return request<RulePreset[]>(`/api/rule-presets${qs}`);
  },
  getRulePreset(id: string) {
    return request<RulePreset>(`/api/rule-presets/${id}`);
  },
  createRulePreset(dto: {
    sportId: string;
    name: string;
    config: Record<string, unknown>;
    isDefault?: boolean;
  }) {
    return request<RulePreset>('/api/rule-presets', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  updateRulePreset(
    id: string,
    dto: {
      name?: string;
      config?: Record<string, unknown>;
      isDefault?: boolean;
    },
  ) {
    return request<RulePreset>(`/api/rule-presets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },
  deleteRulePreset(id: string) {
    return request<{ id: string; deleted: true }>(
      `/api/rule-presets/${id}`,
      { method: 'DELETE' },
    );
  },
  startGame(id: string) {
    return request<GameSummary>(`/api/games/${id}/start`, { method: 'POST' });
  },
  endGame(id: string) {
    return request<GameSummary>(`/api/games/${id}/end`, { method: 'POST' });
  },
  appendEvent(
    gameId: string,
    body: { eventType: string; payload?: Record<string, unknown> },
  ) {
    return request<{ event: unknown; state: ProjectedGameState }>(
      `/api/games/${gameId}/events`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },
  undoLast(gameId: string) {
    return request<{ undone: unknown; state: ProjectedGameState }>(
      `/api/games/${gameId}/undo`,
      { method: 'POST' },
    );
  },
  undoEvent(gameId: string, eventId: string) {
    return request<{ undone: unknown; state: ProjectedGameState }>(
      `/api/games/${gameId}/events/${eventId}/undo`,
      { method: 'POST' },
    );
  },
  recentStatEvents(gameId: string) {
    return request<
      Array<{
        id: string;
        eventType: string;
        payload: Record<string, unknown> | null;
        createdAt: string;
      }>
    >(`/api/games/${gameId}/stat-events`);
  },
  setStatsEnabled(gameId: string, enabled: boolean) {
    return request<{ id: string; statsEnabled: boolean }>(
      `/api/games/${gameId}/stats-enabled`,
      { method: 'POST', body: JSON.stringify({ enabled }) },
    );
  },
  setAnimationConfig(
    gameId: string,
    body: { enabled?: boolean; disabled?: string[] },
  ) {
    return request<{
      id: string;
      animationConfig: { enabled?: boolean; disabled?: string[] } | null;
    }>(`/api/games/${gameId}/animation-config`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  // ─── Remote pairing (admin side) ───
  createRemoteCode(gameId: string, role: 'SCORER' | 'STATS') {
    return request<{
      id: string;
      code: string;
      role: 'SCORER' | 'STATS';
      codeExpiresAt: string;
    }>(`/api/games/${gameId}/remote-codes`, {
      method: 'POST',
      body: JSON.stringify({ role }),
    });
  },
  listRemoteCodes(gameId: string) {
    return request<
      Array<{
        id: string;
        role: 'SCORER' | 'STATS';
        code: string;
        status: 'pending' | 'active' | 'expired';
        claimedAt: string | null;
        codeExpiresAt: string;
        deviceLabel: string | null;
        createdAt: string;
      }>
    >(`/api/games/${gameId}/remote-codes`);
  },
  revokeRemoteSession(sessionId: string) {
    return request<{ id: string; revoked: true }>(
      `/api/remote-sessions/${sessionId}`,
      { method: 'DELETE' },
    );
  },
  // ─── Layouts (scoreboard builder) ───
  listLayouts() {
    return request<LayoutTemplate[]>('/api/layouts');
  },
  createLayout(dto: {
    name: string;
    aspectRatio?: string;
    zones: LayoutZone[];
    bindings: Record<string, LayoutBinding>;
    background?: LayoutBackground | null;
    theme?: LayoutTheme | null;
  }) {
    return request<LayoutTemplate>('/api/layouts', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  updateLayout(
    id: string,
    dto: Partial<{
      name: string;
      aspectRatio: string;
      zones: LayoutZone[];
      bindings: Record<string, LayoutBinding>;
      background: LayoutBackground | null;
      theme: LayoutTheme | null;
    }>,
  ) {
    return request<LayoutTemplate>(`/api/layouts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },
  deleteLayout(id: string) {
    return request<{ id: string; deleted: true }>(`/api/layouts/${id}`, {
      method: 'DELETE',
    });
  },
  // ─── Remote pairing (device side, public claim) ───
  claimRemoteCode(code: string, deviceLabel?: string) {
    return request<{
      accessToken: string;
      gameId: string;
      role: 'SCORER' | 'STATS';
      game: { id: string; homeTeam: string; awayTeam: string; sport: string };
    }>('/api/remote/claim', {
      method: 'POST',
      auth: false,
      body: JSON.stringify({ code, deviceLabel }),
    });
  },
  // ─── Broadcast overlay token ───
  overlayStatus(gameId: string) {
    return request<{ hasToken: boolean; createdAt: string | null }>(
      `/api/games/${gameId}/overlay-token`,
    );
  },
  createOverlayToken(gameId: string) {
    return request<{ token: string; gameId: string }>(
      `/api/games/${gameId}/overlay-token`,
      { method: 'POST' },
    );
  },
  revokeOverlayToken(gameId: string) {
    return request<{ gameId: string; revoked: true }>(
      `/api/games/${gameId}/overlay-token`,
      { method: 'DELETE' },
    );
  },
  // ─── Super-admin ───
  listPendingOrgs() {
    return request<PendingOrg[]>('/api/superadmin/orgs/pending');
  },
  approveOrg(id: string) {
    return request<PendingOrg>(`/api/superadmin/orgs/${id}/approve`, {
      method: 'POST',
    });
  },
  suspendOrg(id: string) {
    return request<PendingOrg>(`/api/superadmin/orgs/${id}/suspend`, {
      method: 'POST',
    });
  },
  unsuspendOrg(id: string) {
    return request<PendingOrg>(`/api/superadmin/orgs/${id}/unsuspend`, {
      method: 'POST',
    });
  },
  // ─── Members + invitations ───
  listMembers() {
    return request<
      Array<{
        id: string;
        email: string;
        name: string;
        role: UserRole;
        isPlatformAdmin: boolean;
        lastLoginAt: string | null;
        createdAt: string;
      }>
    >('/api/invitations/members');
  },
  listInvitations() {
    return request<
      Array<{
        id: string;
        email: string;
        role: UserRole;
        expiresAt: string;
        createdAt: string;
        invitedBy: { id: string; name: string; email: string } | null;
      }>
    >('/api/invitations');
  },
  createInvitation(dto: { email: string; role?: UserRole }) {
    return request<{
      invitation: {
        id: string;
        email: string;
        role: UserRole;
        expiresAt: string;
        createdAt: string;
      };
      token: string;
    }>('/api/invitations', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  revokeInvitation(id: string) {
    return request<{ id: string; revoked: true }>(
      `/api/invitations/${id}`,
      { method: 'DELETE' },
    );
  },
  lookupInvitation(token: string) {
    return request<{
      id: string;
      email: string;
      role: UserRole;
      org: { name: string; slug: string; status: OrgStatus };
      expiresAt: string;
    }>(`/api/invitations/by-token/${encodeURIComponent(token)}`, {
      auth: false,
    });
  },
  acceptInvitation(token: string, dto: { name: string; password: string }) {
    return request<{ id: string; email: string }>(
      `/api/invitations/by-token/${encodeURIComponent(token)}/accept`,
      { method: 'POST', auth: false, body: JSON.stringify(dto) },
    );
  },
  // ─── Ads ───
  listCampaigns() {
    return request<Campaign[]>('/api/ads/campaigns');
  },
  getCampaign(id: string) {
    return request<CampaignWithCreatives>(`/api/ads/campaigns/${id}`);
  },
  createCampaign(dto: CreateCampaignBody) {
    return request<Campaign>('/api/ads/campaigns', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  updateCampaign(id: string, dto: Partial<CreateCampaignBody>) {
    return request<Campaign>(`/api/ads/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },
  deleteCampaign(id: string) {
    return request<{ id: string; deleted: true }>(
      `/api/ads/campaigns/${id}`,
      { method: 'DELETE' },
    );
  },
  createCreative(campaignId: string, dto: CreateCreativeBody) {
    return request<Creative>(`/api/ads/campaigns/${campaignId}/creatives`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  deleteCreative(id: string) {
    return request<{ id: string; deleted: true }>(
      `/api/ads/creatives/${id}`,
      { method: 'DELETE' },
    );
  },
  campaignPerformance(id: string) {
    return request<CampaignPerformance>(`/api/ads/campaigns/${id}/performance`);
  },
  // ─── Tournaments ───
  listTournaments() {
    return request<TournamentSummary[]>('/api/tournaments');
  },
  getTournament(id: string) {
    return request<TournamentDetail>(`/api/tournaments/${id}`);
  },
  createTournament(dto: {
    name: string;
    sportId: string;
    format: TournamentFormat;
    venueId?: string;
    rulePresetId?: string;
    startDate?: string;
    endDate?: string;
  }) {
    return request<TournamentSummary>('/api/tournaments', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  addTournamentEntry(id: string, dto: { teamId: string; groupName?: string }) {
    return request<TournamentEntry>(`/api/tournaments/${id}/entries`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  removeTournamentEntry(id: string, entryId: string) {
    return request<{ id: string; deleted: true }>(
      `/api/tournaments/${id}/entries/${entryId}`,
      { method: 'DELETE' },
    );
  },
  generateBracket(id: string, seededTeamIds: string[]) {
    return request<TournamentDetail>(`/api/tournaments/${id}/generate`, {
      method: 'POST',
      body: JSON.stringify({ seededTeamIds }),
    });
  },
  scheduleTournamentMatch(matchId: string, venueId: string) {
    return request<{ gameId: string }>(
      `/api/tournaments/matches/${matchId}/schedule`,
      { method: 'POST', body: JSON.stringify({ venueId }) },
    );
  },
};

export type WidgetKind =
  | 'score'
  | 'gameClock'
  | 'shotClock'
  | 'period'
  | 'teamName'
  | 'teamLogo'
  | 'possession'
  | 'bonus'
  | 'timeouts'
  | 'teamFouls'
  | 'playerStatsTable'
  | 'teamStatsSummary'
  | 'mediaZone'
  | 'messageBanner'
  | 'sponsorBanner';

export interface LayoutZone {
  id: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LayoutBinding {
  kind: WidgetKind;
  side?: 'home' | 'away';
  options?: Record<string, unknown>;
}

export interface LayoutBackground {
  type: 'theme' | 'solid' | 'gradient' | 'image';
  color?: string;
  from?: string;
  to?: string;
  imageUrl?: string;
  fit?: 'cover' | 'contain';
  overlay?: number;
}

export type ThemePreset = 'classic' | 'maroon' | 'navy' | 'forest' | 'royal';
export type FontKey = 'default' | 'condensed' | 'mono' | 'rounded';
export type FrameStyle = 'flat' | 'rounded' | 'angular' | 'borderless';

export interface LayoutTheme {
  preset?: ThemePreset;
  bg?: string;
  ac?: string;
  br?: string;
  lbl?: string;
  sub?: string;
  font?: FontKey;
  frame?: FrameStyle;
  digitStyle?: DigitStyle;
}

export interface LayoutTemplate {
  id: string;
  orgId: string;
  name: string;
  aspectRatio: string;
  zones: LayoutZone[];
  bindings: Record<string, LayoutBinding>;
  background?: LayoutBackground | null;
  theme?: LayoutTheme | null;
  createdAt: string;
  updatedAt: string;
}

export type TournamentFormat =
  | 'SINGLE_ELIMINATION'
  | 'DOUBLE_ELIMINATION'
  | 'ROUND_ROBIN'
  | 'GROUP_BRACKET';

export type TournamentStatus = 'DRAFT' | 'SEEDING' | 'LIVE' | 'COMPLETED';

export type TournamentMatchStatus =
  | 'PENDING'
  | 'READY'
  | 'LIVE'
  | 'FINAL'
  | 'BYE';

export interface TournamentSummary {
  id: string;
  orgId: string;
  name: string;
  format: TournamentFormat;
  status: TournamentStatus;
  startDate: string | null;
  endDate: string | null;
  sport: { id: string; name: string; slug: string };
  _count: { entries: number; matches: number };
}

export interface TournamentEntry {
  id: string;
  tournamentId: string;
  teamId: string;
  seed: number;
  groupName: string | null;
  team: {
    id: string;
    name: string;
    abbreviation: string | null;
    colorPrimary: string | null;
  };
}

export interface TournamentMatch {
  id: string;
  tournamentId: string;
  gameId: string | null;
  round: number;
  matchInRound: number;
  bracket: string | null;
  groupName: string | null;
  homeEntry: TournamentEntry | null;
  awayEntry: TournamentEntry | null;
  winnerEntry: { id: string; team: { id: string; name: string } } | null;
  status: TournamentMatchStatus;
  scheduledAt: string | null;
  game: {
    id: string;
    status: GameStatus;
    currentState: ProjectedGameState | null;
  } | null;
}

export interface TournamentDetail extends TournamentSummary {
  entries: TournamentEntry[];
  matches: TournamentMatch[];
}

export type CampaignStatus =
  | 'DRAFT'
  | 'ACTIVE'
  | 'PAUSED'
  | 'COMPLETED'
  | 'EXHAUSTED';

export type AdCreativeType = 'IMAGE' | 'VIDEO' | 'HTML5' | 'SPONSORED_OVERLAY';

export interface CreateCampaignBody {
  advertiserName: string;
  contactEmail?: string;
  startDate: string;
  endDate: string;
  budgetCents?: number;
  impressionCap?: number;
  priorityWeight?: number;
  sportFilter?: string[];
  venueFilter?: string[];
  status?: CampaignStatus;
}

export interface CreateCreativeBody {
  type: AdCreativeType;
  mediaUrl: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
  zoneTargets?: string[];
}

export interface Creative {
  id: string;
  campaignId: string;
  type: AdCreativeType;
  mediaUrl: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  zoneTargets: string[];
  createdAt: string;
}

export interface Campaign {
  id: string;
  orgId: string;
  advertiserName: string;
  contactEmail: string | null;
  startDate: string;
  endDate: string;
  budgetCents: number | null;
  impressionCap: number | null;
  priorityWeight: number;
  sportFilter: string[];
  venueFilter: string[];
  status: CampaignStatus;
  createdAt: string;
  updatedAt: string;
  creatives?: Creative[];
  _count?: { impressions: number };
}

export interface CampaignWithCreatives extends Campaign {
  creatives: Creative[];
}

export interface CampaignPerformance {
  campaignId: string;
  advertiserName: string;
  status: CampaignStatus;
  impressionCap: number | null;
  totals: { impressions: number; durationSeconds: number };
  byCreative: Record<string, { impressions: number; durationSeconds: number }>;
  byVenue: Record<string, { impressions: number; durationSeconds: number }>;
  byDay: Record<string, { impressions: number; durationSeconds: number }>;
}

export { ApiError };
